import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./UserOrdersSection.css";
import { useAuth } from "../contexts/AuthContext";
import { useNotification } from "../contexts/NotificationContext";
import { useBusinessContext } from "../contexts/BusinessContext";
import { usePricing } from "../contexts/PricingContext";
import { businessApi, BusinessOrder } from "../services/businessApi";
import {
  generateThemeStudioUrl,
  generateThemeStudioUrlWithToken,
} from "../utils/themeStudio";

type OrderStatusVariant = "success" | "warning" | "danger" | "neutral" | "info";

type BadgeConfig = {
  label: string;
  variant: OrderStatusVariant;
};

const STATUS_BADGES: Record<string, BadgeConfig> = {
  draft: { label: "Draft", variant: "neutral" },
  submitted: { label: "Submitted", variant: "info" },
  completed: { label: "Completed", variant: "success" },
  cancelled: { label: "Cancelled", variant: "danger" },
};

const PAYMENT_BADGES: Record<string, BadgeConfig> = {
  pending: { label: "Pending", variant: "info" },
  paid: { label: "Paid", variant: "success" },
  failed: { label: "Failed", variant: "danger" },
  refunded: { label: "Refunded", variant: "warning" },
};

const PAYMENT_STATUS_MESSAGES: Record<string, string> = {
  paid: "Payment approved! Your order is now paid.",
  failed: "Payment failed. Please try again.",
  refunded: "Your payment has been refunded.",
  pending: "Payment is being processed...",
};

const ORDER_STATUS_MESSAGES: Record<string, string> = {
  completed: "Order completed successfully!",
  cancelled: "Order has been cancelled.",
  submitted: "Order submitted for processing.",
};

interface UserOrdersSectionProps {
  isOpen: boolean;
  onClose: () => void;
}

const UserOrdersSection: React.FC<UserOrdersSectionProps> = ({ isOpen, onClose }) => {
  const { user, isAuthenticated } = useAuth();
  const { showSuccess, showError, showInfo } = useNotification();
  const { restoreOrderDetails, openPaymentDialog } = useBusinessContext();
  const pricing = usePricing();

  const notificationsRef = useRef({
    showSuccess,
    showError,
    showInfo,
  });

  useEffect(() => {
    notificationsRef.current = {
      showSuccess,
      showError,
      showInfo,
    };
  }, [showSuccess, showError, showInfo]);

  const [orders, setOrders] = useState<BusinessOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [polling, setPolling] = useState(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const previousOrdersRef = useRef<BusinessOrder[]>([]);

  const formatCurrency = useCallback((amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  }, []);

  const formatDate = useCallback((value: string) => {
    try {
      return new Date(value).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return value;
    }
  }, []);

  const getStatusBadge = useCallback((status: string) => {
    const config = STATUS_BADGES[status?.toLowerCase()] ?? {
      label: status,
      variant: "neutral" as OrderStatusVariant,
    };
    return config;
  }, []);

  const getPaymentBadge = useCallback((status: string) => {
    const config = PAYMENT_BADGES[status?.toLowerCase()] ?? {
      label: status,
      variant: "neutral" as OrderStatusVariant,
    };
    return config;
  }, []);

  const applyBadgeClass = (variant: OrderStatusVariant) =>
    `user-orders__badge user-orders__badge--${variant}`;

  const checkForStatusChanges = useCallback(
    (oldOrders: BusinessOrder[], newOrders: BusinessOrder[]) => {
      if (!oldOrders?.length || !newOrders?.length) return;

      oldOrders.forEach((previous) => {
        const current = newOrders.find((order) => order.id === previous.id);
        if (!current) return;

        if (previous.payment_status !== current.payment_status) {
          const message =
            PAYMENT_STATUS_MESSAGES[current.payment_status] ||
            `Payment status updated to ${current.payment_status}`;
          notificationsRef.current.showInfo(`${current.site_name}: ${message}`);
        }

        if (previous.status !== current.status) {
          const message =
            ORDER_STATUS_MESSAGES[current.status] ||
            `Order status updated to ${current.status}`;
          notificationsRef.current.showInfo(`${current.site_name}: ${message}`);
        }
      });
    },
    []
  );

  const loadOrders = useCallback(
    async (opts: { showRefreshLoader?: boolean; isPolling?: boolean } = {}) => {
      if (!isAuthenticated) return;

      if (opts.showRefreshLoader) {
        setRefreshing(true);
      } else if (!opts.isPolling) {
        setLoading(true);
      }

      try {
        const response = await businessApi.getUserOrders(1, 10);
        if (response.success && response.data) {
          const nextOrders = response.data.orders ?? [];

          if (opts.isPolling && previousOrdersRef.current.length > 0) {
            checkForStatusChanges(previousOrdersRef.current, nextOrders);
          }

          setOrders(nextOrders);
          previousOrdersRef.current = nextOrders;
        } else if (!response.success) {
          notificationsRef.current.showError(
            response.message || "Failed to load orders"
          );
        }
      } catch (error) {
        console.error("Failed to load orders:", error);
        notificationsRef.current.showError(
          "Failed to load orders. Please try again."
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [checkForStatusChanges, isAuthenticated]
  );

  const startPolling = useCallback(() => {
    if (pollingRef.current) return;
    setPolling(true);
    pollingRef.current = setInterval(() => {
      loadOrders({ isPolling: true });
    }, 10000);
  }, [loadOrders]);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setPolling(false);
  }, []);

  useEffect(() => {
    if (isAuthenticated && isOpen) {
      loadOrders();
      startPolling();
    } else {
      stopPolling();
      if (!isOpen) {
        pollingRef.current = null;
      }
    }

    return () => {
      stopPolling();
    };
  }, [isAuthenticated, isOpen, loadOrders, startPolling, stopPolling]);

  const isOrderReady = useCallback((order: BusinessOrder) => {
    return (
      order.payment_status?.toLowerCase() === "paid" &&
      order.status?.toLowerCase() === "completed"
    );
  }, []);

  const copyPlatformUrl = useCallback(
    async (order: BusinessOrder) => {
      // Check if order is completed and payment is paid
      if (!isOrderReady(order)) {
        showError(
          "Please wait for your order to be completed and payment to be verified before accessing your platform."
        );
        return;
      }

      try {
        // Use API-backed token generation so copied URL matches the editor URL
        const url = await generateThemeStudioUrlWithToken({
          siteName: order.site_name,
        });
        await navigator.clipboard.writeText(url);
        showSuccess("Platform URL copied to clipboard.");
      } catch (error) {
        console.error("Failed to copy platform URL:", error);
        showError("Failed to copy platform URL. Please try again.");
      }
    },
    [showError, showSuccess, isOrderReady]
  );

  const openPlatformEditor = useCallback(
    async (order: BusinessOrder) => {
      // Check if order is completed and payment is paid
      if (!isOrderReady(order)) {
        showError(
          "Please wait for your order to be completed and payment to be verified before accessing your platform."
        );
        return;
      }

      try {
        const url = await generateThemeStudioUrlWithToken({
          siteName: order.site_name,
        });
        window.open(url, "_blank", "noopener,noreferrer");
        showSuccess("Opening platform editor...");
      } catch (error) {
        console.error("Failed to open platform editor:", error);
        showError("Unable to open platform editor. Please try again.");
      }
    },
    [showError, showSuccess, isOrderReady]
  );

  const handlePayNow = useCallback(
    (order: BusinessOrder) => {
      // Find the pricing plan from context
      const pricingPlan = pricing?.plans?.find(
        (plan) => plan.id === order.selected_plan_id || plan.name === order.selected_plan
      );
      
      // Restore order details to context
      restoreOrderDetails(order, pricingPlan);
      
      // Close orders dialog and open payment dialog
      onClose();
      openPaymentDialog();
    },
    [pricing, restoreOrderDetails, onClose, openPaymentDialog]
  );

  const renderOrders = useMemo(() => {
    if (!orders.length) {
      return (
        <div className="user-orders__empty">
          <div className="user-orders__empty-icon">🛒</div>
          <h3>No orders yet</h3>
          <p>Start your business journey by creating your first order.</p>
        </div>
      );
    }

    return orders.map((order) => {
      const statusBadge = getStatusBadge(order.status);
      const paymentBadge = getPaymentBadge(order.payment_status);

      return (
        <article className="user-order-card" key={order.id}>
          <div className="user-order-card__header">
            <div>
              <p className="user-order-card__name">{order.site_name}</p>
              <p className="user-order-card__meta">
                Created {formatDate(order.created_at)}
              </p>
            </div>
            <div className="user-order-card__badges">
              <span className={applyBadgeClass(statusBadge.variant)}>
                {statusBadge.label}
              </span>
              <span className={applyBadgeClass(paymentBadge.variant)}>
                {paymentBadge.label}
              </span>
              {order.payment_status === "pending" && polling && (
                <span className="user-orders__badge user-orders__badge--ghost">
                  <span className="user-orders__dot" />
                  Checking…
                </span>
              )}
            </div>
          </div>

          <div className="user-order-card__grid">
            <div>
              <span className="label">Plan</span>
              <p>{order.selected_plan}</p>
            </div>
            <div>
              <span className="label">Country</span>
              <p>{order.country || "—"}</p>
            </div>
            <div>
              <span className="label">Amount</span>
              <p>{formatCurrency(order.total_amount)}</p>
            </div>
          </div>

          <div className="user-order-card__grid user-order-card__grid--secondary">
            {order.payment_method && (
              <div>
                <span className="label">Payment Method</span>
                <p>{order.payment_method}</p>
              </div>
            )}
            {order.payment_reference && (
              <div>
                <span className="label">Reference</span>
                <p>{order.payment_reference}</p>
              </div>
            )}
            <div>
              <span className="label">Order ID</span>
              <p>{order.order_id || "—"}</p>
            </div>
          </div>

          {/* Payment Information Section */}
          {(order.payment_network_name || order.payment_wallet_address || order.payment_tx_hash) && (
            <div className="user-order-card__payment-info">
              <h4 className="user-order-card__payment-title">Payment Details</h4>
              <div className="user-order-card__payment-grid">
                {order.payment_network_name && (
                  <div>
                    <span className="label">Network</span>
                    <p>
                      {order.payment_network_name}
                      {order.payment_network_symbol && ` (${order.payment_network_symbol})`}
                      {order.payment_network_chain && ` • ${order.payment_network_chain}`}
                    </p>
                  </div>
                )}
                {order.payment_wallet_address && (
                  <div>
                    <span className="label">Wallet Address</span>
                    <p className="user-order-card__address" title={order.payment_wallet_address}>
                      {order.payment_wallet_address.length > 20
                        ? `${order.payment_wallet_address.substring(0, 10)}...${order.payment_wallet_address.substring(order.payment_wallet_address.length - 10)}`
                        : order.payment_wallet_address}
                    </p>
                  </div>
                )}
                {order.payment_tx_hash && (
                  <div>
                    <span className="label">Transaction Hash</span>
                    <p className="user-order-card__tx-hash" title={order.payment_tx_hash}>
                      {order.payment_tx_hash.length > 20
                        ? `${order.payment_tx_hash.substring(0, 10)}...${order.payment_tx_hash.substring(order.payment_tx_hash.length - 10)}`
                        : order.payment_tx_hash}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes Section */}
          {order.notes && (
            <div className="user-order-card__notes">
              <h4 className="user-order-card__notes-title">Notes & Updates</h4>
              <div className="user-order-card__notes-content">
                {order.notes.split('\n\n').map((note, index) => (
                  <p key={index} className="user-order-card__note-item">
                    {note.trim()}
                  </p>
                ))}
              </div>
            </div>
          )}

          <div className="user-order-card__actions">
            {/* Show Pay Now button if payment is pending and no transaction hash exists */}
            {(order.payment_status === "pending" || order.payment_status !== "paid") && !order.payment_tx_hash && (
              <button
                className="user-orders__btn user-orders__btn--pay"
                onClick={() => handlePayNow(order)}
              >
                Pay Now
              </button>
            )}
            <button
              className={`user-orders__btn user-orders__btn--ghost ${!isOrderReady(order) ? 'user-orders__btn--disabled' : ''}`}
              onClick={() => copyPlatformUrl(order)}
              disabled={!isOrderReady(order)}
              title={!isOrderReady(order) ? "Please wait for your order to be completed and payment to be verified" : ""}
            >
              Copy Your Platform URL
            </button>
            <button
              className={`user-orders__btn user-orders__btn--primary ${!isOrderReady(order) ? 'user-orders__btn--disabled' : ''}`}
              onClick={() => openPlatformEditor(order)}
              disabled={!isOrderReady(order)}
              title={!isOrderReady(order) ? "Please wait for your order to be completed and payment to be verified" : ""}
            >
              Edit Platform
            </button>
          </div>
        </article>
      );
    });
  }, [
    orders,
    getStatusBadge,
    getPaymentBadge,
    formatCurrency,
    formatDate,
    polling,
    copyPlatformUrl,
    openPlatformEditor,
    handlePayNow,
    isOrderReady,
  ]);

  if (!isOpen || !isAuthenticated || !user) {
    return null;
  }

  return (
    <div className="orders-dialog-overlay" onClick={onClose}>
      <section className="user-orders" onClick={(e) => e.stopPropagation()}>
        <div className="user-orders__header">
          <div>
            <div className="user-orders__title-row">
              <h2>Your Orders</h2>
              {polling && <span className="user-orders__live-pill">Live Updates</span>}
            </div>
            <p className="user-orders__subtitle">
              Track your business orders and payment status
              {polling && (
                <span className="user-orders__live-text">
                  • Real-time monitoring active
                </span>
              )}
            </p>
          </div>
          <button className="user-orders__close" onClick={onClose} aria-label="Close orders dialog">
            ×
          </button>
          <div className="user-orders__actions">
            <button
              className="user-orders__btn user-orders__btn--ghost"
              onClick={() => loadOrders({ showRefreshLoader: true })}
              disabled={refreshing}
            >
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>
            {polling ? (
              <button
                className="user-orders__btn user-orders__btn--ghost"
                onClick={stopPolling}
              >
                Stop Updates
              </button>
            ) : (
              <button
                className="user-orders__btn user-orders__btn--ghost"
                onClick={startPolling}
              >
                Start Updates
              </button>
            )}
          </div>
        </div>
        {loading ? (
        <div className="user-orders__loading">
          <div className="user-orders__spinner" />
          <p>Loading your business orders…</p>
        </div>
      ) : (
        <div className="user-orders__list">{renderOrders}</div>
      )}
      </section>
    </div>
  );
};

export default UserOrdersSection;

